// Page Tarifs — édition des grilles tarifaires nommées.
// Une grille porte le drapeau « par défaut » : elle s'applique à toute fiche
// qui n'en a pas choisi d'autre. Chaque contact peut ensuite être personnalisé
// depuis sa fiche (bouton "Tarifs du client"), ce qui le détache de sa grille.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  setAuthToken, getTarifsStandard, saveTarifsStandard, getTarifsStandardHistorique,
  getTarifsGrilles, getTarifsGrille, createTarifsGrille, updateTarifsGrille, saveTarifsGrilleData,
} from '../services/api';
import { usePerm } from '../lib/perms';
import { toast } from 'react-toastify';
import { Loader2, Save, Tag, Pencil, X, History } from 'lucide-react';
import TarifsGrid from '../components/TarifsGrid';
import TarifReadOnlyGrid from '../components/TarifReadOnlyGrid';
import ConfirmModal from '../components/ConfirmModal';
import ReglesDeplacement from '../components/ReglesDeplacement';

// Libellés lisibles pour les chemins de l'historique (clé technique -> label).
const SEG = {
  pickup: 'Pickup', livraison: 'Livraison', animation: 'Animation', competences: 'Compétence',
  notations: 'Notation', apporteur_affaire: 'Apporteur', stockage: 'Stockage',
  bareme_km: 'Barème kilométrique', km_par_defaut: 'Km par défaut', forfait_min_km: 'Forfait minimum kilométrique',
  forfait_aller: 'Forfait livraison aller', forfait_retour: 'Forfait livraison retour',
  forfait_installation: 'Forfait installation',
  forfait_desinstallation: 'Forfait désinstallation', forfait_aller_majore: 'Forfait livraison aller majoré',
  forfait_retour_majore: 'Forfait livraison retour majoré', forfait_installation_majore: 'Forfait installation majoré',
  forfait_desinstallation_majore: 'Forfait désinstallation majoré',
  forfait_min_aller: 'Forfait minimum livraison aller', forfait_min_retour: 'Forfait minimum livraison retour',
  installation_fond_vert: 'Installation fond vert / photocall', desinstallation_fond_vert: 'Désinstallation fond vert / photocall',
  spherik: 'Spherik', classik: 'Classik', prestige: 'Prestige',
  photographie: 'Photographie', animateur: 'Animateur', technicien: 'Technicien', depannage_info: 'Dépannage info', deplacements_specifiques: 'Déplacements spécifiques',
  flexibilite: 'Flexibilité', reactivite: 'Réactivité',
  code_promo: 'Code promo', classique: 'Classique', actif: 'Actif', commentaire: 'Commentaire',
  montant_antenne: 'Montant antenne', remise_client: 'Remise client', pourcentage: 'Pourcentage',
};
const prettyPath = (p) => String(p).split('.').map((s) => SEG[s] || s).join(' · ');
const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (typeof v === 'number') return v.toLocaleString('fr-FR');
  return String(v);
};
const fmtDateTime = (d) => { try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

const BTN_LIGHT = {
  padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
  background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
};

/**
 * Petite modale de saisie d'un nom de grille (création, duplication, renommage).
 * Reprend le motif des autres modales de l'app : overlay `modal-overlay`,
 * fermeture au clic extérieur et à la croix. Entrée valide, Échap ferme.
 */
function NomGrilleModal({ titre, aide, valeurInitiale, libelleValider, onValider, onClose }) {
  const [valeur, setValeur] = useState(valeurInitiale || '');
  const vide = !valeur.trim();

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          if (e.key === 'Enter' && !vide) onValider(valeur.trim());
        }}
        style={{ width: '100%', maxWidth: 460, background: 'white', borderRadius: 14, padding: 22 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{titre}</h3>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
            aria-label="Fermer"
          >
            &times;
          </button>
        </div>
        {aide && <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#64748b', lineHeight: 1.45 }}>{aide}</p>}

        <label style={{ display: 'block', fontSize: 12.5, color: '#475569', marginBottom: 6 }}>
          Nom de la grille
        </label>
        <input
          autoFocus
          value={valeur}
          onChange={(e) => setValeur(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeholder="Tarif 2026"
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} style={BTN_LIGHT}>Annuler</button>
          <button
            type="button"
            onClick={() => !vide && onValider(valeur.trim())}
            disabled={vide}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: vide ? '#c7d2fe' : '#4f46e5', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: vide ? 'not-allowed' : 'pointer',
            }}
          >
            {libelleValider}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Tarifs() {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const canEdit = usePerm('tarifs_standard.edit');
  // Grilles nommées : la page édite celle qui est sélectionnée.
  const [grilles, setGrilles] = useState([]);
  const [grilleId, setGrilleId] = useState(null);
  const grille = grilles.find((g) => g.id === grilleId) || null;

  useEffect(() => {
    if (auth?.user?.access_token) setAuthToken(auth.user.access_token);
  }, [auth?.user?.access_token]);

  const loadGrilles = useCallback(
    () =>
      getTarifsGrilles()
        .then((list) => {
          setGrilles(list);
          setGrilleId((cur) => cur || list.find((g) => g.par_defaut)?.id || list[0]?.id || null);
          return list;
        })
        .catch(() => toast.error('Erreur de chargement des grilles')),
    []
  );

  // `id` est toujours explicite : la fonction reste stable, donc utilisable
  // comme dépendance d'effet sans relancer de chargement en boucle.
  const loadData = useCallback((id) => {
    setLoading(true);
    const p = id ? getTarifsGrille(id).then((g) => g?.data || null) : getTarifsStandard();
    return p
      .then((d) => setData(d))
      .catch(() => toast.error('Erreur de chargement de la grille'))
      .finally(() => setLoading(false));
  }, []);

  const loadHistory = useCallback(() => {
    setHistLoading(true);
    return getTarifsStandardHistorique()
      .then((h) => setHistory(Array.isArray(h) ? h : []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []);

  useEffect(() => {
    loadGrilles().then((list) => loadData(list?.find((g) => g.par_defaut)?.id));
    loadHistory();
  }, [loadGrilles, loadData, loadHistory]);

  // Changement de grille : on recharge ses montants
  useEffect(() => {
    if (grilleId) { setEditing(false); loadData(grilleId); }
  }, [grilleId, loadData]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const saved = grilleId ? await saveTarifsGrilleData(grilleId, data) : await saveTarifsStandard(data);
      setData(saved);
      setEditing(false);
      toast.success(`Grille « ${grille?.libelle || 'standard'} » enregistrée`);
      loadGrilles();
      if (grille?.par_defaut) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => { setEditing(false); loadData(grilleId); };

  // Modale de saisie : { mode: 'create' | 'duplicate' | 'rename' }
  const [modale, setModale] = useState(null);

  const creerGrille = async (libelle, duplique) => {
    try {
      const g = await createTarifsGrille({ libelle, sourceId: duplique ? grilleId : null });
      await loadGrilles();
      setGrilleId(g.id);
      setModale(null);
      toast.success(
        duplique
          ? `Grille « ${g.libelle} » créée à partir de « ${grille?.libelle} » — les montants sont à ajuster.`
          : `Grille « ${g.libelle} » créée`
      );
    } catch (e) {
      toast.error(e.response?.data?.error || 'Création impossible');
    }
  };

  const renommer = async (libelle) => {
    if (!grille || libelle === grille.libelle) { setModale(null); return; }
    try {
      await updateTarifsGrille(grille.id, { libelle });
      await loadGrilles();
      setModale(null);
      toast.success('Grille renommée');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Renommage impossible');
    }
  };

  // Rôle et disponibilité de la grille : deux actions distinctes, toutes deux
  // suivies d'un rechargement de la liste (le serveur libère le drapeau
  // précédent et force `actif` sur la nouvelle grille standard).
  const [confirmStandard, setConfirmStandard] = useState(false);
  const [majRole, setMajRole] = useState(false);

  const definirStandard = async () => {
    if (!grille || grille.par_defaut) return;
    setMajRole(true);
    try {
      await updateTarifsGrille(grille.id, { par_defaut: true });
      await loadGrilles();
      loadHistory();
      setConfirmStandard(false);
      toast.success(`« ${grille.libelle} » est désormais le tarif standard.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Changement de tarif standard impossible');
    } finally {
      setMajRole(false);
    }
  };

  const basculerStatut = async () => {
    if (!grille || grille.par_defaut) return;
    const statut = grille.statut === 'actif' ? 'inactif' : 'actif';
    setMajRole(true);
    try {
      await updateTarifsGrille(grille.id, { statut });
      await loadGrilles();
      toast.success(
        statut === 'inactif'
          ? 'Grille désactivée : elle n\'est plus proposable, les fiches qui l\'utilisent la conservent.'
          : 'Grille réactivée'
      );
    } catch (e) {
      toast.error(e.response?.data?.error || 'Changement de statut impossible');
    } finally {
      setMajRole(false);
    }
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1080, margin: '0 auto' }}>
      <header style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{
          width: 40, height: 40, borderRadius: 10, background: '#eef2ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Tag size={20} color="#4f46e5" />
        </span>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Grilles tarifaires</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
            La grille par défaut s'applique à toute fiche qui n'en a pas choisi d'autre.
            Les personnalisations par client se font depuis leur fiche (bouton « Tarifs du client »).
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <select
              value={grilleId || ''}
              onChange={(e) => setGrilleId(Number(e.target.value))}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 220 }}
            >
              {grilles.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.libelle}
                  {g.par_defaut ? ' — par défaut' : ''}
                  {g.statut === 'inactif' ? ' (inactive)' : ''}
                  {g.nb_contacts ? ` · ${g.nb_contacts} fiche(s)` : ''}
                </option>
              ))}
            </select>

            {canEdit && (
              <>
                <button type="button" onClick={() => setModale({ mode: 'duplicate' })} style={BTN_LIGHT}>
                  Dupliquer
                </button>
                <button type="button" onClick={() => setModale({ mode: 'create' })} style={BTN_LIGHT}>
                  Nouvelle grille
                </button>
                <button type="button" onClick={() => setModale({ mode: 'rename' })} style={BTN_LIGHT}>
                  Renommer
                </button>
              </>
            )}
          </div>

          {/* Réglages de la grille sélectionnée. Deux décisions distinctes,
              énoncées avec leur conséquence : le drapeau « standard » déplace
              des fiches, le statut ne fait que retirer la grille des choix
              futurs. */}
          {grille && (
            <div style={{
              marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '12px 14px', background: '#fbfcfe',
              display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#334155', cursor: canEdit && !grille.par_defaut ? 'pointer' : 'default' }}>
                  <input
                    type="radio"
                    checked={!!grille.par_defaut}
                    disabled={!canEdit || grille.par_defaut || majRole}
                    onChange={() => setConfirmStandard(true)}
                    style={{ width: 'auto', margin: 0, accentColor: '#4f46e5' }}
                  />
                  Tarif standard actuel
                </label>
                <p style={{ margin: '4px 0 0 24px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                  {grille.par_defaut
                    ? `Il n'y a qu'un tarif standard à la fois. ${grille.nb_sans_grille || 0} fiche(s) le suivent sans avoir choisi de grille.`
                    : `Le désigner ici basculera sur ces montants les ${grille.nb_sans_grille || 0} fiche(s) qui suivent le tarif standard.`}
                </p>
              </div>

              <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#334155', cursor: canEdit && !grille.par_defaut ? 'pointer' : 'default' }}>
                  <input
                    type="checkbox"
                    checked={grille.statut === 'actif'}
                    disabled={!canEdit || grille.par_defaut || majRole}
                    onChange={basculerStatut}
                    style={{ width: 'auto', margin: 0, accentColor: '#4f46e5' }}
                  />
                  Grille active
                </label>
                <p style={{ margin: '4px 0 0 24px', fontSize: 12, color: grille.statut === 'inactif' ? '#b45309' : '#64748b', lineHeight: 1.5 }}>
                  {grille.par_defaut
                    ? 'Le tarif standard reste toujours actif : il sert de filet aux fiches sans grille.'
                    : grille.statut === 'actif'
                      ? `Proposable sur les fiches contacts.${grille.nb_contacts ? ` ${grille.nb_contacts} fiche(s) l'utilisent.` : ''}`
                      : 'Retirée des choix : les nouvelles fiches ne la voient plus, celles qui l\'utilisent déjà la conservent.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </header>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={26} className="spin" style={{ color: '#6366f1' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 0.9s linear infinite; }`}</style>
        </div>
      )}

      {!loading && data && !editing && (
        <>
          {canEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-perm="antennes:tarifs_standard.edit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '9px 18px', borderRadius: 10, border: 'none',
                  background: '#4f46e5', color: 'white', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                }}
              >
                <Pencil size={15} /> Modifier le tarif
              </button>
            </div>
          )}
          <TarifReadOnlyGrid data={data} />

          {/* Historique des modifications du tarif standard */}
          <div style={{ marginTop: 28 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' }}>
              <History size={18} color="#6366f1" /> Historique des modifications
            </h2>
            {/* L'historique n'est alimenté que par les routes /standard, donc
                pour la grille par défaut : l'afficher ailleurs laisserait
                croire qu'il concerne la grille consultée. */}
            {!grille?.par_defaut ? (
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                L&apos;historique n&apos;est tenu que pour le tarif standard.
              </p>
            ) : histLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <Loader2 size={20} className="spin" style={{ color: '#6366f1' }} />
              </div>
            ) : history.length === 0 ? (
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Aucune modification enregistrée pour le moment.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {history.map((h) => (
                  <div key={h.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{h.user_name || 'Utilisateur'}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(h.created_at)}</span>
                    </div>
                    <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(h.changes || []).map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                          <span style={{ color: '#475569', fontWeight: 600, minWidth: 200 }}>{prettyPath(c.path)}</span>
                          <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{fmtVal(c.old)}</span>
                          <span style={{ color: '#94a3b8' }}>→</span>
                          <span style={{ color: '#92400e', fontWeight: 700, background: '#fef3c7', padding: '1px 8px', borderRadius: 6 }}>{fmtVal(c.new)}</span>
                        </div>
                      ))}
                      {(!h.changes || h.changes.length === 0) && (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Aucun champ détaillé.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {!loading && data && editing && (
        <>
          <TarifsGrid data={data} onChange={setData} readonly={false} />
          <div style={{ position: 'sticky', bottom: 16, marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 10, border: '1px solid #e2e8f0',
                background: 'white', color: '#475569', fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              <X size={15} /> Annuler
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-perm="antennes:tarifs_standard.edit"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 10,
                border: 'none', background: '#4f46e5', color: 'white',
                fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? <Loader2 size={15} className="spin" /> : <Save size={15} />}
              Enregistrer {grille ? `« ${grille.libelle} »` : "le tarif"}
            </button>
          </div>
        </>
      )}

      {confirmStandard && grille && (
        <ConfirmModal
          titre="Définir comme tarif standard"
          message={`« ${grille.libelle} » deviendra le tarif standard : les ${grille.nb_sans_grille || 0} fiche(s) qui suivent le standard sans avoir choisi de grille passeront sur ces montants.`}
          detail={
            `L'ancien tarif standard reste une grille ordinaire, et les fiches qui l'ont explicitement choisie la conservent. `
            + (grille.statut === 'inactif' ? 'Cette grille sera réactivée au passage. ' : '')
            + 'Les fiches personnalisées ne bougent pas : elles sont détachées de toute grille.'
          }
          libelleConfirmer="Définir comme standard"
          busy={majRole}
          onConfirm={definirStandard}
          onClose={() => setConfirmStandard(false)}
        />
      )}

      {/* Monté après le premier chargement : le jeton d'API est alors posé. */}
      {!loading && <ReglesDeplacement canEdit={canEdit} />}

      {modale && (
        <NomGrilleModal
          titre={
            modale.mode === 'rename'
              ? 'Renommer la grille'
              : modale.mode === 'duplicate'
                ? 'Dupliquer la grille'
                : 'Nouvelle grille tarifaire'
          }
          aide={
            modale.mode === 'duplicate'
              ? `Les montants de « ${grille?.libelle} » seront recopiés dans la nouvelle grille. Aucune fiche n'y est rattachée tant que vous ne l'avez pas choisie sur une fiche.`
              : modale.mode === 'create'
                ? 'La grille démarre vide. Aucune fiche n\'y est rattachée tant que vous ne l\'avez pas choisie sur une fiche.'
                : 'Le nom est celui proposé sur les fiches contact.'
          }
          valeurInitiale={
            modale.mode === 'rename'
              ? grille?.libelle || ''
              : modale.mode === 'duplicate'
                ? `${grille?.libelle || 'Grille'} (copie)`
                : 'Tarif 2026'
          }
          libelleValider={modale.mode === 'rename' ? 'Renommer' : 'Créer'}
          onValider={(v) => (modale.mode === 'rename' ? renommer(v) : creerGrille(v, modale.mode === 'duplicate'))}
          onClose={() => setModale(null)}
        />
      )}
    </div>
  );
}
